import { CommissionSettings } from '@/contexts/dynamic-config';
import {
  GetOrderEstimatedCommissionQuery,
  GetOrderEstimatedCommissionQueryVariables,
} from '@/types';
import { gql } from '@apollo/client';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const GET_ORDER_ESTIMATED_COMMISSION_QUERY = gql`
  query GetOrderEstimatedCommission(
    $businessOrganizationUUID: uuid!
    $employeeUUID: uuid!
    $orderUUID: uuid!
  ) {
    getOrderEstimatedCommission(
      business_organization_id: $businessOrganizationUUID
      employee_id: $employeeUUID
      order_ids: [$orderUUID]
    ) {
      commissions {
        estimated_commission_in_micros
        gross_profit_in_micros
        order_id
      }
    }
  }
`;

/**
 * Retrieve the estimated commission for a specific order
 *
 * @param orderUUID - The UUID of the order
 * @param employeeUUID - The UUID of the employee
 * @param businessOrganizationUUID - The UUID of the business organization
 * @param commissionSettings - The commission settings for the user
 *
 * @returns The estimated commission in micros and loading state
 */
export const useGetOrderEstimatedCommission = (
  orderUUID: string,
  employeeUUID: string,
  businessOrganizationUUID: string,
  commissionSettings: CommissionSettings | null,
) => {
  const { data, loading, error, refetch } = useWrappedQuery<
    GetOrderEstimatedCommissionQuery,
    GetOrderEstimatedCommissionQueryVariables
  >(GET_ORDER_ESTIMATED_COMMISSION_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip:
      !commissionSettings ||
      !commissionSettings.draft_order_commission_enabled ||
      !employeeUUID,
    variables: {
      businessOrganizationUUID,
      employeeUUID,
      orderUUID,
    },
  });

  // Retrieve the first (and only) commission from the data
  const commission = data?.getOrderEstimatedCommission?.commissions?.[0];

  return {
    error,
    estimatedCommissionInMicros:
      commission?.estimated_commission_in_micros ?? null,
    grossProfitInMicros: commission?.gross_profit_in_micros ?? null,
    loading,
    refetch,
  };
};
